import { Link } from "react-router-dom";
import {
  Phone,
  Zap,
  Truck,
  BatteryCharging,
  CircleDot,
  ShieldAlert,
  Package,
  Clock,
  Wrench,
  MessageSquare,
  UserCheck,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  PHONE_DISPLAY,
  HERO_IMG,
  ABOUT_IMG,
  SERVICE_IMG_ROADSIDE,
} from "../lib/constants";
import { CallButton, QuoteButton, SectionLabel } from "../components/Shared";

const TRUST_POINTS = [
  "24/7 emergency response",
  "Fast local recovery",
  "Friendly, professional drivers",
  "Cars, vans & light commercial",
];

const SERVICES = [
  {
    icon: Wrench,
    title: "Roadside Assistance",
    desc: "For non-starting vehicles, minor roadside issues, warning lights and unexpected faults.",
  },
  {
    icon: Truck,
    title: "Vehicle Recovery",
    desc: "Safe transport for cars, vans and light commercial vehicles to your home, garage or chosen destination.",
  },
  {
    icon: BatteryCharging,
    title: "Jump Starts",
    desc: "Fast support for flat batteries, battery checks and getting your vehicle started again.",
  },
  {
    icon: CircleDot,
    title: "Tyre & Wheel Support",
    desc: "Help with punctures, damaged tyres and spare wheel fitting where suitable.",
  },
  {
    icon: ShieldAlert,
    title: "Accident & Breakdown Transport",
    desc: "Careful recovery after breakdowns, kerb damage, blowouts or vehicle faults.",
  },
  {
    icon: Package,
    title: "Commercial Vehicle Recovery",
    desc: "Support for trades, couriers and small businesses that need their vehicle back in operation quickly.",
  },
];

const WHY = [
  {
    icon: Clock,
    title: "Fast response when it matters",
    desc: "Breakdowns are stressful. Our job is to make the next step simple, calm and quick.",
  },
  {
    icon: Wrench,
    title: "Professional recovery equipment",
    desc: "Your vehicle is loaded, secured and transported with care.",
  },
  {
    icon: MessageSquare,
    title: "Clear communication",
    desc: "We keep you updated so you know who is coming, what happens next and where your vehicle is going.",
  },
  {
    icon: UserCheck,
    title: "Friendly, capable drivers",
    desc: "The best recovery service is not just fast. It is reassuring, respectful and practical.",
  },
];

const REVIEWS = [
  {
    t: "Quick arrival times",
    d: "Nobody wants to be left waiting, especially at night, with children, or before an appointment.",
  },
  {
    t: "Calm, helpful technicians",
    d: "A reassuring person makes a stressful situation easier.",
  },
  {
    t: "Clear updates",
    d: "Drivers want to know the ETA, the issue and the next step.",
  },
  {
    t: "Problem-solving at the roadside",
    d: "When possible, the best outcome is getting the vehicle moving again without unnecessary delay.",
  },
  {
    t: "Safe recovery when repair is not possible",
    d: "If the vehicle cannot be fixed roadside, it needs careful transport to the right place.",
  },
];

const STEPS = [
  {
    n: "01",
    t: "Call us",
    d: "Tell us where you are and what has happened.",
  },
  {
    n: "02",
    t: "We assess the issue",
    d: "We confirm the vehicle type, location and best recovery option.",
  },
  {
    n: "03",
    t: "Help is dispatched",
    d: "A recovery driver is sent with the right equipment.",
  },
  {
    n: "04",
    t: "You're recovered safely",
    d: "We take your vehicle to your home, garage or preferred destination.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-white/10"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="ExpressBreakdown recovery in action"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-36 lg:py-44">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 bg-brand text-black px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em]">
              <span className="relative flex h-2 w-2 pulse-dot rounded-full bg-black" />
              Live Dispatch · 24/7
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-8 text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold tracking-tighter leading-[0.95]"
            >
              Breakdown recovery
              <br />
              that gets you moving
              <br />
              <span className="text-brand">again fast.</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed"
            >
              ExpressBreakdown provides reliable roadside assistance, vehicle
              recovery, jump starts, tyre support and local transport recovery
              when you need help most.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <CallButton
                testId="hero-call-btn"
                label={`Call Now for Recovery`}
              />
              <QuoteButton testId="hero-quote-btn" />
            </div>

            <ul className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
              {TRUST_POINTS.map((t) => (
                <li
                  key={t}
                  data-testid={`trust-point-${t.slice(0, 10)}`}
                  className="flex items-start gap-2 text-xs sm:text-sm text-white/80"
                >
                  <CheckCircle2
                    className="h-4 w-4 text-brand shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="relative border-t border-white/10 bg-black/80 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs uppercase tracking-[0.24em] text-white/60">
              Cars · Vans · Light Commercial
            </div>
            <a
              href="tel:+447717759310"
              className="text-sm font-bold text-brand hover:underline"
              data-testid="hero-strip-phone"
            >
              {PHONE_DISPLAY} →
            </a>
          </div>
        </div>
      </section>

      {/* EMERGENCY SECTION */}
      <section
        data-testid="emergency-section"
        className="relative border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 text-brand">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
                <span className="text-xs uppercase tracking-[0.28em] font-bold">
                  Emergency Advice
                </span>
              </div>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                Broken down? Stay safe.
                <br />
                <span className="text-brand">Call ExpressBreakdown now.</span>
              </h2>
              <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
                We'll confirm your location, assess the issue and send the
                right recovery support as quickly as possible.
              </p>
              <div className="mt-8">
                <CallButton testId="emergency-call-btn" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-brand bg-black p-8">
                <div className="text-xs uppercase tracking-[0.24em] text-brand font-bold">
                  Emergency Line
                </div>
                <a
                  href="tel:+447717759310"
                  className="block mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight hover:text-brand transition-colors"
                  data-testid="emergency-phone-large"
                >
                  {PHONE_DISPLAY}
                </a>
                <div className="mt-6 space-y-2 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-brand"
                      strokeWidth={2.5}
                    />
                    Answered by a real person
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-brand"
                      strokeWidth={2.5}
                    />
                    Rapid assessment & dispatch
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className="h-4 w-4 text-brand"
                      strokeWidth={2.5}
                    />
                    Clear pricing & next steps
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="services-section"
        className="relative border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <SectionLabel>What we do</SectionLabel>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                Services built for stressful moments.
              </h2>
            </div>
            <Link
              to="/services"
              className="text-sm font-bold uppercase tracking-wider text-white/70 hover:text-brand flex items-center gap-2"
              data-testid="services-see-all"
            >
              See all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group bg-black p-10 hover:bg-[#0f0f0f] transition-colors relative"
              >
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 flex items-center justify-center border border-white/20 group-hover:border-brand group-hover:bg-brand group-hover:text-black transition-colors">
                    <s.icon className="h-5 w-5" strokeWidth={2.25} />
                  </div>
                  <div className="text-xs font-bold text-white/30 tracking-wider">
                    0{i + 1}
                  </div>
                </div>
                <h3 className="mt-8 font-display font-bold text-xl sm:text-2xl tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        data-testid="why-section"
        className="relative border-b border-white/10 bg-[#0f0f0f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionLabel>Why choose us</SectionLabel>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                Fast. Professional.
                <br />
                <span className="text-brand">Reassuring.</span>
              </h2>
              <p className="mt-6 text-base text-white/70 leading-relaxed max-w-md">
                Every breakdown is different. Our service is shaped around the
                things drivers actually need in the moment.
              </p>
              <div className="mt-10">
                <QuoteButton testId="why-quote-btn" label="Talk to us" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
                {WHY.map((w, i) => (
                  <div
                    key={w.title}
                    data-testid={`why-card-${i}`}
                    className="bg-[#0f0f0f] p-8"
                  >
                    <w.icon
                      className="h-6 w-6 text-brand"
                      strokeWidth={2.25}
                    />
                    <h3 className="mt-5 font-display font-bold text-lg">
                      {w.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed">
                      {w.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS THEMES */}
      <section
        data-testid="reviews-section"
        className="relative border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <SectionLabel>What drivers value</SectionLabel>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              What drivers value most in a recovery service.
            </h2>
            <p className="mt-6 text-base text-white/70 leading-relaxed">
              Based on common themes from major UK breakdown service reviews,
              drivers care about:
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {REVIEWS.map((r, i) => (
              <div
                key={r.t}
                data-testid={`review-theme-${i}`}
                className="border border-white/10 p-6 hover:border-brand transition-colors"
              >
                <div className="font-display font-extrabold text-4xl text-brand">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-6 font-display font-bold text-base leading-tight">
                  {r.t}
                </h3>
                <p className="mt-3 text-xs text-white/60 leading-relaxed">
                  {r.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        data-testid="process-section"
        className="relative border-b border-white/10 bg-[#0f0f0f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <SectionLabel>Process</SectionLabel>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                How it works.
              </h2>
            </div>
            <CallButton
              testId="process-call-btn"
              label="Start your recovery"
            />
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`process-step-${i}`}
                className="bg-[#0f0f0f] p-10 relative"
              >
                <div className="font-display font-extrabold text-6xl text-brand/20 leading-none">
                  {s.n}
                </div>
                <h3 className="mt-6 font-display font-bold text-xl">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM REASSURANCE */}
      <section
        data-testid="reassurance-section"
        className="relative border-b border-white/10 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={ABOUT_IMG}
            alt="Recovery truck"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-2xl">
            <SectionLabel>Built for stress</SectionLabel>
            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              Built for stressful moments.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed">
              A breakdown can ruin your day. ExpressBreakdown is designed to
              make it feel manageable again: quick contact, clear pricing,
              professional handling and safe recovery.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="relative bg-brand text-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.28em] font-bold">
                Need help right now?
              </div>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-6xl font-display font-extrabold tracking-tighter">
                Need breakdown recovery now?
              </h2>
              <p className="mt-6 text-base sm:text-lg max-w-2xl font-medium">
                Call ExpressBreakdown and get help on the way.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <a
                href="tel:+447717759310"
                data-testid="final-cta-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-black text-brand font-bold uppercase tracking-wider text-base px-7 py-5 hover:bg-[#0f0f0f] transition-colors"
              >
                <Phone className="h-5 w-5" strokeWidth={2.75} />
                Call Now
              </a>
              <a
                href="tel:+447717759310"
                data-testid="final-cta-quote-btn"
                className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-black text-black font-bold uppercase tracking-wider text-base px-7 py-5 hover:bg-black hover:text-brand transition-colors"
              >
                Request a Quote
                <ArrowRight className="h-5 w-5" strokeWidth={2.75} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
